import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Edit2, Printer, BookOpen, DollarSign,
  CheckCircle, TrendingUp, Loader2,
  Phone, MapPin, Heart, User, ChevronDown, Key, Copy, Send,
  Calendar, Activity, GraduationCap,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';

const TABS = ['Overview', 'Attendance', 'Results', 'Fees'];

const STATUS_COLOR = {
  present: 'text-green-700 bg-green-50 border border-green-200',
  absent: 'text-red-700 bg-red-50 border border-red-200',
  late: 'text-amber-700 bg-amber-50 border border-amber-200',
};

const GRADE_COLOR = {
  A1: '#16a34a', B2: '#2563eb', B3: '#2563eb',
  C4: '#d97706', C5: '#d97706', C6: '#d97706',
  D7: '#ea580c', E8: '#ea580c', F9: '#dc2626',
};

export default function StudentDetailPage() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('Overview');
  const [sendingCreds, setSendingCreds] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/students/${studentId}/summary`)
      .then(d => { if (d.success) setSummary(d.data); })
      .catch(err => toast.error(err?.detail || 'Failed to load student'))
      .finally(() => setLoading(false));
  }, [studentId]);

  const handleSendCredentials = async () => {
    setSendingCreds(true);
    try {
      const res = await api.post(`/students/${studentId}/send-credentials`, {});
      toast.success(res.message || 'Credentials sent to parent email!', { duration: 5000 });
    } catch (err) {
      toast.error(err?.detail || 'Failed to send credentials');
    } finally {
      setSendingCreds(false);
    }
  };

  const handlePrint = () => {
    if (!summary) return;
    const { student, attendance, results, fees } = summary;
    const fullName = `${student.first_name} ${student.last_name}`;
    const printWindow = window.open('', '_blank', 'width=800,height=1000');
    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Student Profile – ${fullName}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Segoe UI', Arial, sans-serif; background: #fff; color: #1e293b; font-size: 13px; }
    .card { max-width: 680px; margin: 20px auto; border: 1.5px solid #e2e8f0; border-radius: 12px; overflow: hidden; }
    .header { background: #2563eb; color: white; padding: 24px 28px; display: flex; align-items: center; gap: 20px; }
    .avatar-circle { width: 72px; height: 72px; border-radius: 12px; border: 3px solid rgba(255,255,255,0.6); background: #3b82f6; display: flex; align-items: center; justify-content: center; font-size: 24px; font-weight: 800; color: white; flex-shrink: 0; overflow: hidden; }
    .avatar-circle img { width: 100%; height: 100%; object-fit: cover; }
    .header-text h1 { font-size: 22px; font-weight: 700; margin-bottom: 4px; }
    .header-text p { opacity: 0.8; font-size: 13px; }
    .kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 1px; background: #e2e8f0; border-top: 1px solid #e2e8f0; border-bottom: 1px solid #e2e8f0; }
    .kpi { background: #f8fafc; padding: 12px 16px; text-align: center; }
    .kpi-val { font-size: 18px; font-weight: 700; color: #1e293b; }
    .kpi-label { font-size: 10px; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 2px; }
    .section { padding: 20px 28px; border-bottom: 1px solid #f1f5f9; }
    .section h2 { font-size: 10px; text-transform: uppercase; letter-spacing: 1px; color: #94a3b8; font-weight: 700; margin-bottom: 12px; }
    .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 24px; }
    .field { display: flex; justify-content: space-between; align-items: center; padding: 6px 0; border-bottom: 1px solid #f1f5f9; }
    .field-label { color: #64748b; font-size: 11px; font-weight: 600; }
    .field-value { font-weight: 600; color: #1e293b; font-size: 12px; }
    .login-box { background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 8px; padding: 10px 14px; margin-top: 12px; }
    .login-box .login-title { font-size: 10px; color: #2563eb; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px; }
    .login-box .login-row { display: flex; justify-content: space-between; font-size: 12px; }
    .login-box .login-row span { font-weight: 600; color: #1d4ed8; }
    .footer { padding: 10px 28px; text-align: center; font-size: 10px; color: #94a3b8; background: #f8fafc; }
    @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  </style>
</head>
<body>
  <div class="card">
    <div class="header">
      ${student.avatar
        ? `<div class="avatar-circle"><img src="${student.avatar}" alt="${fullName}" /></div>`
        : `<div class="avatar-circle">${(student.first_name?.[0] || '') + (student.last_name?.[0] || '')}</div>`
      }
      <div class="header-text">
        <h1>${fullName}</h1>
        <p>${student.class_name || ''} ${student.section ? '· Section ' + student.section : ''} · Adm: ${student.admission_number || ''}</p>
        <p>${student.gender || ''} · ${student.date_of_birth || 'DOB not set'}</p>
      </div>
    </div>
    <div class="kpis">
      <div class="kpi"><div class="kpi-val">${attendance.rate}%</div><div class="kpi-label">Attendance</div></div>
      <div class="kpi"><div class="kpi-val">${results.overall_avg ? results.overall_avg + '%' : '—'}</div><div class="kpi-label">Avg Score</div></div>
      <div class="kpi"><div class="kpi-val">₦${fees.outstanding.toLocaleString()}</div><div class="kpi-label">Outstanding</div></div>
      <div class="kpi"><div class="kpi-val">${student.blood_group || '—'}</div><div class="kpi-label">Blood Group</div></div>
    </div>
    <div class="section">
      <h2>Personal Information</h2>
      <div class="grid2">
        <div>
          ${[['First Name', student.first_name], ['Last Name', student.last_name], ['Gender', student.gender], ['Date of Birth', student.date_of_birth || '—']]
            .map(([l, v]) => `<div class="field"><span class="field-label">${l}</span><span class="field-value">${v}</span></div>`).join('')}
        </div>
        <div>
          ${[['Class', student.class_name || '—'], ['Section', student.section || '—'], ['Admission No.', student.admission_number], ['Blood Group', student.blood_group || '—']]
            .map(([l, v]) => `<div class="field"><span class="field-label">${l}</span><span class="field-value">${v}</span></div>`).join('')}
        </div>
      </div>
      ${student.username ? `
      <div class="login-box">
        <div class="login-title">Student Login Credentials</div>
        <div class="login-row"><span>Username:</span><span>${student.username}</span></div>
        <div class="login-row" style="margin-top:3px"><span>Default Password:</span><span>Student's phone number</span></div>
      </div>` : ''}
    </div>
    <div class="section">
      <h2>Guardian / Parent</h2>
      <div class="field"><span class="field-label">Guardian Name</span><span class="field-value">${student.parent_name || '—'}</span></div>
      <div class="field"><span class="field-label">Phone</span><span class="field-value">${student.parent_phone || '—'}</span></div>
      <div class="field"><span class="field-label">Address</span><span class="field-value">${student.address || '—'}</span></div>
      ${student.medical_notes ? `<div class="field"><span class="field-label">Medical Notes</span><span class="field-value" style="color:#dc2626">${student.medical_notes}</span></div>` : ''}
    </div>
    <div class="footer">Printed on ${new Date().toLocaleDateString('en-NG', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} · EDUVA School Management System</div>
  </div>
  <script>window.onload = function(){ window.print(); window.onafterprint = function(){ window.close(); }; };</script>
</body>
</html>`;
    printWindow.document.write(html);
    printWindow.document.close();
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
    </div>
  );

  if (!summary) return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-slate-500 gap-3">
      <GraduationCap className="w-12 h-12 text-slate-300" />
      <p className="font-medium">Student not found</p>
      <button onClick={() => navigate('/school/students')} className="text-blue-600 text-sm hover:underline">
        Back to Students
      </button>
    </div>
  );

  const { student, attendance, results, fees } = summary;
  const fullName = `${student.first_name} ${student.last_name}`;
  const initials = `${student.first_name?.[0] || ''}${student.last_name?.[0] || ''}`;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="student-detail-page">

      {/* ── Hero ── */}
      <div className="bg-[#2563eb] relative overflow-hidden">
        {/* Subtle dot pattern */}
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage: `radial-gradient(circle, #fff 1px, transparent 1px)`,
            backgroundSize: '20px 20px',
          }}
        />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Top bar */}
          <div className="flex items-center justify-between py-4">
            <button
              onClick={() => navigate('/school/students')}
              className="flex items-center gap-2 text-white/70 hover:text-white text-sm font-medium transition-colors"
              data-testid="back-to-students"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Students
            </button>

            <div className="flex items-center gap-2 flex-wrap justify-end">
              <button
                onClick={() => navigate(`/school/students?edit=${studentId}`)}
                className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-sm font-medium px-3 py-1.5 rounded-lg border border-white/20 transition-all"
                data-testid="edit-student-btn"
              >
                <Edit2 className="w-3.5 h-3.5" />
                Edit
              </button>
              <button
                onClick={handlePrint}
                className="flex items-center gap-1.5 bg-white text-blue-700 hover:bg-blue-50 text-sm font-semibold px-3 py-1.5 rounded-lg transition-all shadow-sm"
                data-testid="print-student-btn"
              >
                <Printer className="w-3.5 h-3.5" />
                Print Profile
              </button>
              {student.username && (
                <button
                  onClick={handleSendCredentials}
                  disabled={sendingCreds}
                  className="flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-white text-sm font-semibold px-3 py-1.5 rounded-lg transition-all shadow-sm"
                  data-testid="send-credentials-btn"
                >
                  {sendingCreds
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <Send className="w-3.5 h-3.5" />
                  }
                  {sendingCreds ? 'Sending…' : 'Send to Parent'}
                </button>
              )}
            </div>
          </div>

          {/* Student identity */}
          <div className="flex items-end gap-5 pb-8 pt-2">
            <div className="flex-shrink-0">
              {student.avatar ? (
                <img
                  src={student.avatar}
                  alt={fullName}
                  className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl ring-4 ring-white/30 shadow-xl object-cover"
                />
              ) : (
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl ring-4 ring-white/30 shadow-xl bg-blue-500 flex items-center justify-center">
                  <span className="text-2xl sm:text-3xl font-extrabold text-white">{initials}</span>
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0 pb-1">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight">{fullName}</h1>
                <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${
                  student.status === 'active'
                    ? 'bg-emerald-400/25 text-emerald-100 border border-emerald-400/40'
                    : 'bg-white/10 text-white/60 border border-white/20'
                }`}>
                  {student.status}
                </span>
              </div>
              <p className="text-blue-100 text-sm">
                {student.class_name || 'No Class'}
                {student.section ? ` · Section ${student.section}` : ''}
                {student.admission_number ? ` · Adm: ${student.admission_number}` : ''}
              </p>
              {student.gender && (
                <p className="text-blue-200 text-xs mt-0.5">
                  {student.gender}{student.date_of_birth ? ` · Born ${student.date_of_birth}` : ''}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">

        {/* KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <KpiCard
            icon={Activity}
            label="Attendance"
            value={`${attendance.rate}%`}
            sub={`${attendance.present} / ${attendance.total} days`}
            accent="blue"
            data-testid="kpi-attendance"
          />
          <KpiCard
            icon={TrendingUp}
            label="Avg Score"
            value={results.overall_avg ? `${results.overall_avg}%` : '—'}
            sub={`${results.terms.length} term${results.terms.length !== 1 ? 's' : ''}`}
            accent="violet"
            data-testid="kpi-avg-score"
          />
          <KpiCard
            icon={DollarSign}
            label="Outstanding"
            value={`₦${fees.outstanding.toLocaleString()}`}
            sub={`of ₦${fees.total.toLocaleString()}`}
            accent={fees.outstanding > 0 ? 'red' : 'green'}
            data-testid="kpi-outstanding"
          />
          <KpiCard
            icon={BookOpen}
            label="Subjects"
            value={results.terms[0]?.subjects?.length || '—'}
            sub="this term"
            accent="amber"
            data-testid="kpi-subjects"
          />
        </div>

        {/* Tabs Panel */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="border-b border-slate-100 px-2 sm:px-4">
            <div className="flex gap-0.5">
              {TABS.map(t => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={`px-4 py-3 text-sm font-semibold border-b-2 transition-all ${
                    tab === t
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-200'
                  }`}
                  data-testid={`tab-${t.toLowerCase()}`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div className="p-5 sm:p-6">
            {tab === 'Overview'   && <OverviewTab student={student} />}
            {tab === 'Attendance' && <AttendanceTab attendance={attendance} />}
            {tab === 'Results'    && <ResultsTab results={results} />}
            {tab === 'Fees'       && <FeesTab fees={fees} />}
          </div>
        </div>

      </div>
    </div>
  );
}

/* ── KPI Card ─────────────────────────────────────────────────────── */
function KpiCard({ icon: Icon, label, value, sub, accent, 'data-testid': testId }) {
  const styles = {
    blue:   { wrap: 'border-blue-100',   icon: 'bg-blue-100 text-blue-600',   val: 'text-blue-700' },
    violet: { wrap: 'border-violet-100', icon: 'bg-violet-100 text-violet-600', val: 'text-violet-700' },
    red:    { wrap: 'border-red-100',    icon: 'bg-red-100 text-red-600',     val: 'text-red-700' },
    green:  { wrap: 'border-green-100',  icon: 'bg-green-100 text-green-600', val: 'text-green-700' },
    amber:  { wrap: 'border-amber-100',  icon: 'bg-amber-100 text-amber-600', val: 'text-amber-700' },
  };
  const s = styles[accent] || styles.blue;
  return (
    <div className={`bg-white rounded-2xl border ${s.wrap} p-4 shadow-sm hover:shadow-md transition-shadow`} data-testid={testId}>
      <div className={`w-9 h-9 rounded-xl ${s.icon} flex items-center justify-center mb-3`}>
        <Icon className="w-4 h-4" />
      </div>
      <p className={`text-xl font-extrabold ${s.val} leading-tight`}>{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mt-1.5">{label}</p>
    </div>
  );
}

/* ── Tab: Overview ────────────────────────────────────────────────── */
function OverviewTab({ student }) {
  const personalFields = [
    { label: 'First Name',    value: student.first_name },
    { label: 'Last Name',     value: student.last_name },
    { label: 'Gender',        value: student.gender || '—' },
    { label: 'Date of Birth', value: student.date_of_birth || '—' },
    { label: 'Blood Group',   value: student.blood_group || '—' },
    { label: 'Class',         value: student.class_name || '—' },
    { label: 'Section',       value: student.section || '—' },
    { label: 'Admission No.', value: student.admission_number || '—' },
  ];

  const guardianFields = [
    { icon: User,  label: 'Name',    value: student.parent_name || student.guardian_name || '—' },
    { icon: Phone, label: 'Phone',   value: student.parent_phone || student.guardian_phone || '—' },
    { icon: MapPin, label: 'Address', value: student.address || student.guardian_address || '—' },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6" data-testid="overview-tab-content">

      {/* Personal info – 3 cols */}
      <div className="lg:col-span-3">
        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Personal Information</p>
        <div className="grid grid-cols-2 gap-3">
          {personalFields.map(f => (
            <div key={f.label} className="bg-slate-50 rounded-xl px-4 py-3 border border-slate-100">
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">{f.label}</p>
              <p className="text-sm font-bold text-slate-800">{f.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Guardian + credentials – 2 cols */}
      <div className="lg:col-span-2 space-y-4">

        {/* Guardian */}
        <div>
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Guardian / Parent</p>
          <div className="bg-slate-50 rounded-xl border border-slate-100 overflow-hidden" data-testid="guardian-card">
            {guardianFields.map((g, i) => (
              <div key={g.label} className={`flex items-center gap-3 px-4 py-3 ${i > 0 ? 'border-t border-slate-100' : ''}`}>
                <div className="w-7 h-7 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <g.icon className="w-3.5 h-3.5 text-blue-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wide">{g.label}</p>
                  <p className="text-sm font-semibold text-slate-700 truncate">{g.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Medical notes */}
        {student.medical_notes && (
          <div className="bg-red-50 border border-red-100 rounded-xl p-4 flex gap-3" data-testid="medical-notes">
            <Heart className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-bold text-red-600 uppercase tracking-wide">Medical Notes</p>
              <p className="text-sm text-red-700 mt-1 leading-relaxed">{student.medical_notes}</p>
            </div>
          </div>
        )}

        {/* Login credentials */}
        {student.username ? (
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4" data-testid="credentials-card">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center">
                <Key className="w-3.5 h-3.5 text-white" />
              </div>
              <p className="text-xs font-bold text-blue-700 uppercase tracking-wide">Student Login</p>
            </div>
            <CredRow label="Username" value={student.username} mono testId="cred-username" />
            <CredRow
              label="Default Password"
              value={student.phone || student.parent_phone || '(phone at enrolment)'}
              mono
              testId="cred-password"
            />
            <p className="text-[10px] text-blue-400 mt-2 leading-relaxed">
              Initial password set at enrolment. May have been changed since.
            </p>
          </div>
        ) : (
          <div className="bg-slate-50 border border-slate-100 rounded-xl p-4 flex items-center gap-3" data-testid="no-credentials-card">
            <Key className="w-4 h-4 text-slate-300 flex-shrink-0" />
            <p className="text-xs text-slate-400">No login account — edit student and add a phone number to create one</p>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Tab: Attendance ─────────────────────────────────────────────── */
function AttendanceTab({ attendance }) {
  const { total, present, absent, late, rate, recent } = attendance;

  return (
    <div className="space-y-5" data-testid="attendance-tab-content">
      {/* Summary tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total Days', value: total,   cls: 'bg-slate-50 border-slate-200 text-slate-700' },
          { label: 'Present',    value: present, cls: 'bg-green-50 border-green-200 text-green-700' },
          { label: 'Absent',     value: absent,  cls: 'bg-red-50 border-red-200 text-red-700' },
          { label: 'Late',       value: late,    cls: 'bg-amber-50 border-amber-200 text-amber-700' },
        ].map(s => (
          <div key={s.label} className={`rounded-xl p-4 border text-center ${s.cls}`}>
            <p className="text-2xl font-extrabold leading-none">{s.value}</p>
            <p className="text-xs font-semibold mt-1.5 opacity-70 uppercase tracking-wide">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Progress bar */}
      <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-slate-700">Attendance Rate</span>
          <span className={`text-sm font-extrabold ${rate >= 75 ? 'text-green-600' : rate >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
            {rate}%
          </span>
        </div>
        <div className="h-2.5 bg-slate-200 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-700 ${rate >= 75 ? 'bg-green-500' : rate >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
            style={{ width: `${rate}%` }}
          />
        </div>
      </div>

      {/* Recent records */}
      {recent.length > 0 ? (
        <div className="overflow-hidden rounded-xl border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                <th className="text-left px-4 py-2.5 text-xs font-bold text-slate-500 uppercase">Date</th>
                <th className="text-left px-4 py-2.5 text-xs font-bold text-slate-500 uppercase">Status</th>
                <th className="text-left px-4 py-2.5 text-xs font-bold text-slate-500 uppercase hidden sm:table-cell">Marked By</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((r, i) => (
                <tr key={r.id || i} className={`border-b border-slate-50 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
                  <td className="px-4 py-2.5 font-medium text-slate-700">
                    {new Date(r.date).toLocaleDateString('en-NG', { weekday: 'short', day: 'numeric', month: 'short' })}
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full capitalize ${STATUS_COLOR[r.status] || 'text-slate-600 bg-slate-100'}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-400 hidden sm:table-cell">{r.marked_by || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12 text-slate-400">
          <Calendar className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm font-medium">No attendance records yet</p>
        </div>
      )}
    </div>
  );
}

/* ── Tab: Results ────────────────────────────────────────────────── */
function ResultsTab({ results }) {
  const { terms, overall_avg } = results;
  const [openTerm, setOpenTerm] = useState(terms[0]?.term_name || '');

  if (!terms.length) return (
    <div className="text-center py-12 text-slate-400">
      <BookOpen className="w-10 h-10 mx-auto mb-3 opacity-30" />
      <p className="text-sm font-medium">No results recorded yet</p>
    </div>
  );

  return (
    <div className="space-y-3" data-testid="results-tab-content">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500 font-semibold">
          {terms.length} term{terms.length !== 1 ? 's' : ''} of results
        </p>
        <span className="text-sm font-bold text-blue-600">Overall Avg: {overall_avg}%</span>
      </div>

      {terms.map(term => (
        <div key={term.term_name} className="border border-slate-200 rounded-xl overflow-hidden">
          <button
            onClick={() => setOpenTerm(o => o === term.term_name ? '' : term.term_name)}
            className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors"
            data-testid={`term-accordion-${term.term_name}`}
          >
            <div className="flex items-center gap-3">
              <span className="font-bold text-slate-700 text-sm">{term.term_name}</span>
              <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${
                term.avg >= 60 ? 'bg-green-100 text-green-700' :
                term.avg >= 45 ? 'bg-amber-100 text-amber-700' :
                'bg-red-100 text-red-700'
              }`}>
                Avg {term.avg}%
              </span>
            </div>
            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${openTerm === term.term_name ? 'rotate-180' : ''}`} />
          </button>

          {openTerm === term.term_name && (
            <table className="w-full text-sm">
              <thead className="bg-white border-b border-slate-100">
                <tr>
                  <th className="text-left px-4 py-2.5 text-xs font-bold text-slate-500">Subject</th>
                  <th className="text-center px-3 py-2.5 text-xs font-bold text-slate-500">Score</th>
                  <th className="text-center px-3 py-2.5 text-xs font-bold text-slate-500">%</th>
                  <th className="text-center px-3 py-2.5 text-xs font-bold text-slate-500">Grade</th>
                </tr>
              </thead>
              <tbody>
                {term.subjects.map((s, i) => (
                  <tr key={s.subject} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                    <td className="px-4 py-2.5 font-semibold text-slate-700">{s.subject}</td>
                    <td className="px-3 py-2.5 text-center text-slate-600">{s.total}/{s.max}</td>
                    <td className="px-3 py-2.5 text-center font-bold text-slate-800">{s.pct}%</td>
                    <td className="px-3 py-2.5 text-center">
                      <span
                        className="font-extrabold text-sm"
                        style={{ color: GRADE_COLOR[s.components?.[0]?.grade] || '#64748b' }}
                      >
                        {s.components?.[0]?.grade || '—'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ))}
    </div>
  );
}

/* ── Tab: Fees ───────────────────────────────────────────────────── */
function FeesTab({ fees }) {
  const { invoices, total, paid, outstanding } = fees;

  return (
    <div className="space-y-5" data-testid="fees-tab-content">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 text-center">
          <p className="text-lg font-extrabold text-slate-800">₦{total.toLocaleString()}</p>
          <p className="text-xs text-slate-400 font-semibold mt-1 uppercase tracking-wide">Total Billed</p>
        </div>
        <div className="bg-green-50 rounded-xl p-4 border border-green-100 text-center">
          <p className="text-lg font-extrabold text-green-700">₦{paid.toLocaleString()}</p>
          <p className="text-xs text-green-500 font-semibold mt-1 uppercase tracking-wide">Paid</p>
        </div>
        <div className={`${outstanding > 0 ? 'bg-red-50 border-red-100' : 'bg-emerald-50 border-emerald-100'} rounded-xl p-4 border text-center`}>
          <p className={`text-lg font-extrabold ${outstanding > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
            ₦{outstanding.toLocaleString()}
          </p>
          <p className={`text-xs font-semibold mt-1 uppercase tracking-wide ${outstanding > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
            Outstanding
          </p>
        </div>
      </div>

      {invoices.length > 0 ? (
        <div className="overflow-hidden rounded-xl border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                <th className="text-left px-4 py-2.5 text-xs font-bold text-slate-500 uppercase">Fee</th>
                <th className="text-right px-4 py-2.5 text-xs font-bold text-slate-500 uppercase">Amount</th>
                <th className="text-right px-4 py-2.5 text-xs font-bold text-slate-500 uppercase hidden sm:table-cell">Paid</th>
                <th className="text-right px-4 py-2.5 text-xs font-bold text-slate-500 uppercase hidden sm:table-cell">Balance</th>
                <th className="text-center px-4 py-2.5 text-xs font-bold text-slate-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv, i) => (
                <tr key={inv.id} className={`border-b border-slate-50 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
                  <td className="px-4 py-2.5">
                    <p className="font-semibold text-slate-700">{inv.fee_name}</p>
                    {inv.due_date && (
                      <p className="text-xs text-slate-400 mt-0.5">
                        Due {new Date(inv.due_date).toLocaleDateString('en-NG')}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right font-semibold text-slate-700">
                    ₦{parseFloat(inv.amount || 0).toLocaleString()}
                  </td>
                  <td className="px-4 py-2.5 text-right text-green-600 font-semibold hidden sm:table-cell">
                    ₦{parseFloat(inv.paid_amount || 0).toLocaleString()}
                  </td>
                  <td className="px-4 py-2.5 text-right text-red-600 font-semibold hidden sm:table-cell">
                    ₦{parseFloat(inv.balance || 0).toLocaleString()}
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${
                      inv.status === 'paid'    ? 'bg-green-100 text-green-700' :
                      inv.status === 'partial' ? 'bg-amber-100 text-amber-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {inv.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12 text-slate-400">
          <DollarSign className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm font-medium">No fee records</p>
        </div>
      )}
    </div>
  );
}

/* ── CredRow ─────────────────────────────────────────────────────── */
function CredRow({ label, value, mono, testId }) {
  const [copied, setCopied] = React.useState(false);
  const copy = () => {
    if (!value) return;
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div className="flex items-center justify-between py-1.5" data-testid={testId}>
      <span className="text-xs text-blue-500 font-semibold">{label}</span>
      <div className="flex items-center gap-2">
        <span className={`text-sm font-bold text-blue-700 ${mono ? 'font-mono' : ''}`}>{value}</span>
        <button
          onClick={copy}
          className="text-blue-300 hover:text-blue-600 transition-colors"
          title="Copy"
          data-testid={`${testId}-copy-btn`}
        >
          {copied
            ? <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
            : <Copy className="w-3.5 h-3.5" />
          }
        </button>
      </div>
    </div>
  );
}
